import { createFileRoute } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import { Loader2 } from "lucide-react";
import { Card, CardContent } from "@/components/ui/card";
import { RouteError, RouteNotFound } from "@/components/route-boundaries";
import { BousalaReportView } from "@/components/bousala/report/report-view";
import { getPublicBousalaReport } from "@/lib/bousala/public-report.functions";
import { dirFor } from "@/lib/bousala/i18n";

export const Route = createFileRoute("/_public/r/$token")({
  ssr: false,
  head: () => ({
    meta: [
      { title: "تقرير بوصلة د. سارة" },
      { name: "robots", content: "noindex, nofollow, noarchive, nosnippet" },
    ],
  }),
  errorComponent: RouteError,
  notFoundComponent: RouteNotFound,
  component: PublicReportPage,
});

function PublicReportPage() {
  const { token } = Route.useParams();
  const fetchReport = useServerFn(getPublicBousalaReport);

  const q = useQuery({
    queryKey: ["bousala", "public-report", token],
    queryFn: () => fetchReport({ data: { token } }),
  });

  if (q.isLoading) {
    return (
      <main className="flex items-center justify-center py-24" dir="rtl">
        <Loader2 className="h-6 w-6 animate-spin" aria-hidden="true" />
        <span className="sr-only" role="status" aria-live="polite">
          جارٍ تحميل التقرير…
        </span>
      </main>
    );
  }

  if (q.error || !q.data) {
    return (
      <main className="mx-auto max-w-xl px-6 py-20 text-center" dir="rtl">
        <Card>
          <CardContent className="space-y-3 py-10">
            <h1 className="text-2xl font-bold">التقرير غير موجود</h1>
            <p className="text-base leading-loose text-brown-500">
              الرابط غير صحيح أو ما عاد متاح. الأفضل التأكد من نسخ الرابط كامل.
            </p>
          </CardContent>
        </Card>
      </main>
    );
  }

  const data = q.data;
  return (
    <main className="bg-cream-200 py-8 px-4" dir={dirFor(data.language)}>
      {data.language !== "en" && (
        <header className="mx-auto mb-8 max-w-3xl" dir="rtl">
          <p className="font-display text-[13px] font-bold text-amber-800">نتيجة بالبوصلة الدراسية</p>
          <p className="mt-2 font-display text-[36px] font-extrabold leading-tight text-text-primary">
            عرفت شو <span className="mark">بيشبهني</span>.
          </p>
          <p className="mt-2 text-[17px] leading-[1.85] text-text-secondary">8 محاور · 160+ سؤال من مواقف حقيقية · تقرير فوري</p>
        </header>
      )}
      <BousalaReportView
        shareToken={token}
        model={data.model}
        tier={data.tier}
        attemptId=""
        surface="public"
        lang={data.language}
      />
    </main>
  );
}
